import { HelpIcon } from "@dehub/theme"
import { ActionIcon, Avatar, Box, Divider, Flex, Grid, Text, Textarea, TextInput } from "@mantine/core"
import { ImageIcon, TextAaIcon, TrashIcon, TruckIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { IAdditionalLine } from '../../models/AdditionalLine'
import { IAttachmentPlus } from '../../models/Attachment'
import { ILine } from '../../models/Line'
import { IAuth } from '../../store/Auth'
import { currencyFormat, lineNetAmount } from "../../utils"
import { dateTimeSecFormat } from "../../utils/date"
import { renderName } from "../../utils/render-name"
import { ColumnType, Table } from "../table"
import { usePermission } from "../permission"

export function SupplierDropShippingProductForm({ action, setFieldValue, values }: { action: any; setFieldValue: any; values: any }) {
  const { t } = useTranslation(["supplierSales", "common"])
  const { user } = useSelector((state: { auth: IAuth }) => state.auth)
  const ERP_PGH_MARGIN_VIEW = usePermission("ERP_PGH_MARGIN_VIEW")
  const now_date = new Date()
  const showMargin = ERP_PGH_MARGIN_VIEW.hasAccess && ((action && action[1]?.lines) || []).some((l: any) => l?.costPrice != null)
  const columns = useHeader(t, showMargin)
  const columnsAdditional = useHeaderAdditional(t)

  const columnsAttachments = useHeaderAttachments({
    action,
    user,
    t,
    values,
    setFieldValue,
    onClick: (key, item) => {
      if (key === "remove") {
        setFieldValue("attachments", [...values.attachments.filter((i: any, index: number) => item.index !== index)])
      }
    },
  })
  const len = Object.keys((action && action[1]?.lines) || [])?.length

  return (
    <Box
      style={{
        background: "var(--mantine-color-white)",
        borderRadius: 1,
        border: `1px solid var(--mantine-color-gray-2)`,
        padding: "0px 20px",
      }}>
      <Flex mt={20} direction="row" justify="space-between" mb={20}>
        <Text fw={500} c="var(--mantine-color-dark-7)">
          {t("supplierSales:dropshipping.productsSection")}<HelpIcon helpKey="order.supplier-drop-shipping-detail.section.products" variant="section" />
        </Text>
      </Flex>
      <Divider color="gray" my="xs" />
      <Table columns={columns} name="supplier-sales-order-lines.list" dataSource={action && (action[1].lines || [])} pagination={false} />
      <Divider color="gray" my="xs" />
      <Flex mt={20} direction="row" justify="space-between" mb={20}>
        <Text fw={500} c="var(--mantine-color-dark-7)">
          {t("supplierSales:dropshipping.additionalSection")}
        </Text>
      </Flex>
      <Divider color="gray" my="xs" />
      <Table
        columns={columnsAdditional}
        name="supplier-sales-order-list-additional.list"
        dataSource={action && (action[1].additionalLines || [])}
        pagination={false}
      />
      <Divider color="gray" my="xs" />
      <Flex mt={20} direction="row" justify="space-between" mb={20}>
        <Text fw={500} c="var(--mantine-color-dark-7)">
          {t("supplierSales:dropshipping.summarySection")}
        </Text>
      </Flex>
      <Divider color="gray" my="xs" />
      <Grid mb={5}>
        <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
          <Grid>
            <Grid.Col span={12}>
              {action && action[0] === "edit" ? (
                <Textarea
                  label={t("supplierSales:dropshipping.customerNoteLabel")}
                  placeholder={t("supplierSales:customer.customerNotePlaceholder")}
                  required
                  value={values?.senderNote ?? ""}
                  onChange={(e) => setFieldValue("senderNote", e.target.value)}
                />
              ) : (
                <Textarea value={(action && action[1]?.senderNote) || t("supplierSales:dropshipping.customerNoteLabel")} label={t("supplierSales:dropshipping.customerNoteLabel")} onChange={() => { }} disabled />
              )}
            </Grid.Col>
            <Grid.Col span={12}>
              {action && action[0] === "edit" ? (
                <Textarea
                  label={t("supplierSales:dropshipping.additionalNoteLabel")}
                  placeholder={t("supplierSales:customer.additionalNotePlaceholder")}
                  required
                  value={values?.senderAdditionalNote ?? ""}
                  onChange={(e) => setFieldValue("senderAdditionalNote", e.target.value)}
                />
              ) : (
                <Textarea value={(action && action[1]?.senderAdditionalNote) || t("supplierSales:dropshipping.additionalNoteLabel")} label={t("supplierSales:dropshipping.additionalNoteEdit")} onChange={() => { }} disabled />
              )}
            </Grid.Col>
          </Grid>
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
          <Box
            h="100%"
            style={{
              backgroundColor: "var(--mantine-color-white)",
              borderLeft: `2px outset var(--mantine-color-brand-6)`,
            }}
            px={20}
            py={16}>
            <Grid>
              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)">
                  {t("supplierSales:dropshipping.orderTypeCount")}
                </Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                  {len} {t("common:units.type")}
                </Text>
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)">
                  {t("supplierSales:dropshipping.totalQuantity")}
                </Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                  {action[1]?.lines?.reduce((total: number, item: any) => total + item!.quantity || 0, 0) || 0} {t("common:units.piece")}
                </Text>
              </Grid.Col>

              <Grid.Col span={12}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-7)">
                  {t("supplierSales:dropshipping.calculation")}
                </Text>
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)">
                  {t("supplierSales:dropshipping.orderTotalAmount")}
                </Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                  <Text>{currencyFormat(action[1]?.lines?.reduce((total: number, item: any) => total + item!.quantity * item?.price || 0, 0) || 0)}</Text>
                </Text>
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ml={47}>
                  {t("supplierSales:dropshipping.calculatedVat")}
                </Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                  {currencyFormat((action[1]?.lines || [])?.reduce((total: number, item: any) => total + item!.quantity * item?.vatAmount || 0, 0) || 0)}
                </Text>
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ml={47}>
                  {t("supplierSales:dropshipping.calculatedTax")}
                </Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                  {currencyFormat(action[1]?.items?.reduce((total: number, item: any) => total + item!.quantity * item?.taxAmount || 0, 0) || 0)}
                </Text>
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)">
                  {t("supplierSales:dropshipping.calculatedDiscount")}
                </Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                  {currencyFormat(
                    ((action && action[1]?.lines) || [])?.reduce(
                      (total: number, item: any) =>
                        total +
                        (item?.discountType === "PERCENTAGE"
                          ? (item?.price * item?.quantity * item?.discountValue) / 100
                          : item?.quantity * item?.discountValue) || 0,
                      0,
                    ) || 0,
                  )}
                </Text>
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)">
                  {t("supplierSales:dropshipping.shippingFee")}
                </Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                  {currencyFormat(action[1]?.shippingAmount || 0)}
                </Text>
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)">
                  {t("supplierSales:dropshipping.totalDiscount")}
                </Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                  {currencyFormat(action[1]?.discountValue || 0)}
                </Text>
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)">
                  {t("supplierSales:dropshipping.additionalTotal")}
                </Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                  {!action[1]?.additionalLines
                    ? currencyFormat(0)
                    : currencyFormat(
                      (action[1]?.additionalLines || [])?.reduce((total: number, item: any) => total + item!.quantity * item?.price || 0, 0) -
                      (action[1]?.additionalLines || [])?.reduce(
                        (total: number, item: any) =>
                          total +
                          (item?.discountType === "PERCENTAGE"
                            ? ((item?.price || 0) * (item?.quantity || 0) * item.discountValue) / 100
                            : item?.discountValue) || 0,
                        0,
                      ),
                    )}
                </Text>
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)">
                  {t("supplierSales:dropshipping.totalAmount")}
                </Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                  {/* The grand total is decided by de-order-service and stored on the order:
                      ORDER_AMOUNT (lines + additional, no VAT/tax/shipping added)
                      minus the order-level discount, plus shipping. It used to be
                      recomputed here with the VAT and city tax added back on top of
                      already-VAT-inclusive line totals, and with shipping inside the
                      percentage base (DSC-001). */}
                  {currencyFormat(action[1]?.lines?.length ? (action[1]?.totalAmount || 0) : 0)}
                </Text>
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)">
                  {t("supplierSales:dropshipping.paymentConfirm")}
                </Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                {!action[1]?.lines || !action[1]?.paymentTerm ? (
                  <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                    {currencyFormat(0)}
                  </Text>
                ) : action[1]?.paymentTerm?.condition === "COD" ? (
                  <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                    {/* A share of the corrected grand total. */}
                    {currencyFormat(((action[1]?.totalAmount || 0) * (action[1]?.paymentTerm?.advancePercent || 0)) / 100)}
                  </Text>
                ) : (
                  <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                    {"-"}
                  </Text>
                )}
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                <Text size="lg" fw={500} c="var(--mantine-color-dark-5)">
                  {t("supplierSales:dropshipping.paymentConfirmDate")}
                </Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 12, md: 6 }}>
                {action[1]?.paymentTerm?.condition === "COD" ? (
                  <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                    {dateTimeSecFormat(now_date.setDate(now_date.getDate() + action[1]?.paymentTerm?.confirmationDay))}
                  </Text>
                ) : (
                  <Text size="lg" fw={500} c="var(--mantine-color-dark-5)" ta="right">
                    {"-"}
                  </Text>
                )}
              </Grid.Col>
            </Grid>
          </Box>
        </Grid.Col>
      </Grid>
      <Divider color="gray" my="xs" />
      <Flex mt={20} direction="row" justify="space-between" mb={20}>
        <Text fw={500} c="var(--mantine-color-dark-7)">
          {t("supplierSales:dropshipping.attachmentSection")}
        </Text>
        <Flex gap="md" align="center">
          {action && action[0] !== "detail" && (
            <ActionIcon variant="outline" size="md" color="var(--mantine-color-brand-6)">
              <TextAaIcon
                onClick={() => {
                  setFieldValue("attachments", [
                    ...(values?.attachments || []),
                    {
                      name: "",
                      url: "",
                      description: "",
                    },
                  ])
                }}
              />
            </ActionIcon>
          )}
        </Flex>
      </Flex>
      <Divider color="gray" my="xs" />
      <Table
        columns={columnsAttachments}
        name="values.attachments"
        dataSource={action && action[0] === "edit" ? (values || []).attachments : (action[1] || []).attachments}
        pagination={false}
      />
      <Divider color="gray" my="xs" />
    </Box>
  )
}

const useHeader = (t: any, showMargin: boolean): ColumnType<ILine>[] => [
  {
    title: "#",
    render: (_r, index) => <Text size="sm">{index + 1}</Text>,
  },
  {
    title: t("supplierSales:dropshipping.columns.image"),
    dataIndex: "image",
    render: (record) => (
      <Avatar src={record?.image || null} radius="xs">
        <ImageIcon />
      </Avatar>
    ),
  },
  {
    title: t("supplierSales:dropshipping.columns.skuCode"),
    dataIndex: "skuCode",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
          {record?.skuCode ?? "-"}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.productAndService"),
    dataIndex: "name",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
          {record?.name ?? "-"}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.measureUnit"),
    dataIndex: "unit.name",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
          {record?.unit ?? "-"}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.unitPrice"),
    dataIndex: "price",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat(record?.price ?? 0)}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.discount"),
    dataIndex: "discountValue",
    render: (record) => {
      let count
      count =
        record?.discountType === "PERCENTAGE"
          ? ((record?.price || 0) * (record?.quantity || 0) * (record.discountValue || 0)) / 100
          : (record?.quantity || 0) * (record?.discountValue || 0)
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat(count ?? 0)}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.orderQuantity"),
    dataIndex: "quantity",
    render: (record) => {
      return (
        <Text size="sm" w="max-content">
          {record.quantity ?? "0"}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.vat"),
    dataIndex: "hasVat",
    render: (record) => {
      let count
      count = (record?.quantity || 0) * (record?.vatAmount || 0)
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat(count ?? 0)}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.tax"),
    dataIndex: "hasTax",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat(record?.taxAmount ?? 0)}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.totalAmount"),
    dataIndex: "name",
    render: (record: any) => {
      // The line total the server stored (post line discount and post the line's
      // share of the order-level discount). It used to add the whole line's VAT
      // on top of a price that already contained it (DSC-001).
      const count = Number(record?.totalAmount) || lineNetAmount(record)
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat(count ?? 0)}
        </Text>
      )
    },
  },
  ...((showMargin ? [
    {
      title: t("supplierSales:dropshipping.costPrice"),
      dataIndex: "costPrice",
      render: (record: any) => (
        <Text size="sm" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat((record?.costPrice || 0) * (record?.quantity || 0))}
        </Text>
      ),
    },
    {
      title: t("supplierSales:dropshipping.soldPrice"),
      dataIndex: "price",
      render: (record: any) => (
        <Text size="sm" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat((record?.price || 0) * (record?.quantity || 0))}
        </Text>
      ),
    },
    {
      title: t("supplierSales:dropshipping.columns.margin"),
      dataIndex: "name",
      render: (record: any) => (
        <Text size="sm" c="green" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat(((record?.price || 0) - (record?.costPrice || 0)) * (record?.quantity || 0))}
        </Text>
      ),
    },
  ] : []) as ColumnType<ILine>[]),
]

const useHeaderAdditional = (t: any): ColumnType<IAdditionalLine>[] => [
  {
    title: "#",
    render: (_r, index) => <Text size="sm">{index + 1}</Text>,
  },
  {
    title: t("supplierSales:dropshipping.columns.itemName"),
    dataIndex: "name",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
          {record?.name ?? "-"}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.itemUnit"),
    dataIndex: "unit.name",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
          {record?.unit ?? "-"}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.itemPrice"),
    dataIndex: "price",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat(record?.price || 0)}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.itemQuantity"),
    dataIndex: "quantity",
    render: (record) => {
      return (
        <Text size="sm" w="max-content">
          {record.quantity ?? "0"}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.discountType"),
    dataIndex: "discountType",
    render: (record) => {
      return (
        <Text size="sm" w="max-content">
          {record.discountType === "PERCENTAGE" && t("supplierSales:dropshipping.columns.discountPercent")}
          {record.discountType === "AMOUNT" && t("supplierSales:dropshipping.columns.discountAmount")}
          {!record?.discountType && "-"}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.itemDiscount"),
    dataIndex: "discountType",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
          {record.discountType === "PERCENTAGE" && `${record?.discountValue} %`}
          {record.discountType === "AMOUNT" && currencyFormat(record?.discountValue ?? 0)}
          {!record?.discountType && "-"}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.itemAmount"),
    dataIndex: "name",
    render: (record: any) => {
      let count
      count =
        (record?.price || 0) * (record?.quantity || 0) -
        (record?.discountType === "PERCENTAGE" ? ((record?.price || 0) * (record?.quantity || 0) * record.discountValue) / 100 : record?.discountValue)
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat(count || 0)}
        </Text>
      )
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.subSo"),
    dataIndex: "",
    render: () => {
      return (
        <ActionIcon>
          <TruckIcon color="var(--mantine-color-brand-6)" />
        </ActionIcon>
      )
    },
  },
]

const useHeaderAttachments = ({ onClick, action, user, t, values, setFieldValue }: { onClick: (e: any, e1: any) => void; action: any; user: any; t: any; values: any; setFieldValue: any }): ColumnType<IAttachmentPlus>[] => [
  {
    title: "#",
    render: (_r, index) => <Text size="sm">{index + 1}</Text>,
  },
  {
    title: t("supplierSales:dropshipping.columns.attachmentName"),
    dataIndex: "name",
    render: (record, index) => {
      if (action && action[0] === "edit") {
        return (
            <TextInput
              w={220}
              value={values?.attachments?.[index]?.name ?? ""}
              onChange={(e) => setFieldValue(`attachments.${index}.name`, e.target.value)}
              placeholder={t("common:form.enterName")}
              disabled={action && action[0] === "detail"}
            />
          )
      } else {
        return (
          <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
            {record?.name ?? "-"}
          </Text>
        )
      }
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.attachmentUrl"),
    dataIndex: "url",
    render: (record, index) => {
      if (action && action[0] === "edit") {
        return (
            <TextInput
              w={220}
              value={values?.attachments?.[index]?.url ?? ""}
              onChange={(e) => setFieldValue(`attachments.${index}.url`, e.target.value)}
              placeholder={t("common:form.enterUrl")}
            />
          )
      } else {
        return (
          <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
            {record?.url ?? "-"}
          </Text>
        )
      }
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.attachmentDate"),
    dataIndex: "description",
    render: (record) => {
      if (action && action[0] === "edit") {
        return (
          <Text size="sm" c="var(--mantine-color-dark-5)">
            {"-"}
          </Text>
        )
      } else {
        return (
          <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
            {dateTimeSecFormat(record?.createdAt)}
          </Text>
        )
      }
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.attachmentDescription"),
    dataIndex: "description",
    render: (record, index) => {
      if (action && action[0] === "edit") {
        return (
            <Textarea
              value={values?.attachments?.[index]?.description ?? ""}
              onChange={(e) => setFieldValue(`attachments.${index}.description`, e.target.value)}
              placeholder={t("common:form.enterDescription")}
              maxLength={100}
            />
          )
      } else {
        return (
          <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
            {record?.url ?? "-"}
          </Text>
        )
      }
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.attachedBy"),
    dataIndex: "description",
    render: (record) => {
      if (action && action[0] === "edit") {
        return (
          <Text size="sm" fw={500} c="var(--mantine-color-dark-5)" w="max-content">
            {renderName(user)}
          </Text>
        )
      } else {
        return (
          <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
            {renderName(record?.attachedUser)}
          </Text>
        )
      }
    },
  },
  {
    title: t("supplierSales:dropshipping.columns.action"),
    align: "right",
    render: (record, index) => {
      return (
        <ActionIcon
          onClick={() => action && action[0] === "edit" && onClick("remove", { ...record, index })}
          color={action && action[0] === "edit" ? "red" : "gray"}>
          <TrashIcon />
        </ActionIcon>
      )
    },
  },
]
